import React, { useState, useRef, useEffect } from 'react';
import { useT } from './i18n/I18nContext';
import { isGroundShippingTitle, normalizeShippingTitle } from './constants/groundShippingMethods';
import { isDesktopBrowser, isIpadLike } from './constants/deviceDetect';

// iPad / iPhone / Android → legacy popup+AirPrint flow (untouched).
// Everything else (desktop Chrome/Safari/Firefox/Edge on Mac/Win/Linux) →
// new in-page modal flow, so fullscreen portal mode isn't broken by a
// focus-stealing popup window. Note: modern desktop Chrome reports
// maxTouchPoints > 0 even without a touchscreen — we CAN'T use that as a
// desktop signal. iPadOS 13+ reports UA as "Macintosh"; `maxTouchPoints > 1`
// flags the iPad (real Macs report 0) so it still routes to the legacy flow.
// 2026-10-14 (Eric): iPadOS 27 stopped reporting maxTouchPoints > 1 under its Mac UA, so iPads
// (Safari tab AND the Home Screen shortcut) were classified as desktops and sent to the in-page modal,
// which never pops AirPrint. The shared detector in constants/deviceDetect.js reads the signals WebKit
// still exposes (navigator.standalone exists only on iOS/iPadOS, touch events, coarse pointer).
function detectIsDesktopPrint() {
    return isDesktopBrowser();
}
const IS_DESKTOP_PRINT = detectIsDesktopPrint();

// Standalone PWA detection (iPad Add-to-Home-Screen / Safari standalone).
// iOS Safari in standalone PWA mode grants window.print() WITHOUT a prompt
// only on the first call per page lifecycle — every subsequent call shows
// "This website has been blocked from automatically printing", regardless
// of user-gesture or cache-busting tricks. Workaround: navigate the parent
// window to the print URL each time. Each navigation creates a fresh
// document, so the auto-print fires as a "first print" and is allowed.
function detectIsStandalonePWA() {
    if (typeof window === 'undefined') return false;
    try {
        if (window.navigator && window.navigator.standalone === true) return true;
        if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    } catch (_) {}
    return false;
}
const IS_STANDALONE_PWA = detectIsStandalonePWA();

// 2026-10-14 (Eric): iPad running the portal in a Safari TAB (iPadOS 27 took printing away from the
// Home Screen app). Safari on iOS prints iframe content blank and prints the HTML label document as a
// scaled web page, so on an iPad tab every label print opens the combined 4x6 PDF in its own tab,
// where iOS's PDF viewer owns the print sheet (Share → Print).
const IS_IPAD_SAFARI_TAB = !IS_STANDALONE_PWA && !IS_DESKTOP_PRINT && isIpadLike();

// Auto-approve ceiling for UPS Ground quotes (2026-08-23): standard-flow
// UPS Ground labels purchase without the "Confirm & Buy Label" tap when the
// quote is at or under this. Quotes above it (or the USPS PO-box fallback)
// still show the confirmation gate so a mis-weighted package gets eyes
// before money moves.
const AUTO_BUY_MAX_USD = 100;

function buildPwaReturnUrl(orderId, storeKey, shopifyLocationId) {
    // The Packing Portal manages the selected order in React state, not in
    // the URL — so window.location.href on /packing-portal/active is just the
    // base portal URL with no order context. Navigating back there after
    // closing the label drops the operator on the search screen instead of
    // the order they were QC'ing. The QCPortal route tree DOES support a
    // per-order URL (/.../packing-portal/active/orders/<id>) and auto-
    // bootstraps that order on mount via useParams. Strip an existing
    // /orders/<id> suffix first (when the operator deep-linked) to avoid
    // double-stacking it.
    //
    // Multi-assignment PIN sessions also need store_key + shopify_location_id
    // in the URL so QCPortal can resolve the order's location without forcing
    // the operator back to the search screen ("Please open this order from
    // the search page" guard). Pass them as query params.
    let pathname = window.location.pathname;
    pathname = pathname.replace(/\/orders\/[^/]+\/?$/, '');
    if (!orderId) return window.location.href;
    const params = new URLSearchParams();
    if (storeKey) params.set('store', storeKey);
    if (shopifyLocationId) params.set('loc', shopifyLocationId);
    const qs = params.toString();
    return `${pathname}/orders/${orderId}${qs ? `?${qs}` : ''}`;
}
function buildPwaPrintUrl(pdfUrl, orderId, storeKey, shopifyLocationId) {
    // Strip any &desktop=1 (which suppresses the popup HTML's auto-print —
    // we WANT the auto-print in PWA-navigation mode), and append return URL
    // so the popup's Close Label button can navigate back to the QC page.
    const cleaned = pdfUrl.replace(/&desktop=1/g, '').replace(/\?desktop=1&?/, '?').replace(/\?$/, '');
    const sep = cleaned.includes('?') ? '&' : '?';
    const ret = buildPwaReturnUrl(orderId, storeKey, shopifyLocationId);
    return `${cleaned}${sep}return=${encodeURIComponent(ret)}`;
}

// 2026-07-29 SP-297179: Under-scan guard helper. Mirrors QCPortal's
// computeTotalOpenUnitsAtLocation and app.py's _total_open_units_at_location
// so the FE/BE both count the SAME set of units when checking whether a
// /complete is truly complete or a partial. Sums remainingQuantity across all
// OPEN/IN_PROGRESS non-virtual non-parked-pickup FOs at the current location.
// 2026-07-30: also excludes individual virtual line items (Free Returns +
// Package Protection / Processing Fee) that piggy-back onto real FOs — the
// packing scan list already skips them, so counting them here would trip the
// under-scan modal. Reported as "orders with Redo can't be packed".
const _VIRTUAL_TITLES_LC = new Set([
    'free returns + package protection',
    'processing fee',
    'team portal handling fee',
]);
function _isVirtualTitle(t) { return _VIRTUAL_TITLES_LC.has((t || '').toLowerCase().trim()); }
function computeTotalOpenUnitsAtLoc(order, shopifyLocationId) {
    if (!shopifyLocationId || !order) return 0;
    const titleByOli = {};
    for (const e of (order.lineItems?.edges || [])) {
        const n = e.node || {};
        if (n.id) titleByOli[n.id] = (n.title || '');
    }
    const locGid = `gid://shopify/Location/${shopifyLocationId}`;
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    let total = 0;
    for (const fo of fos) {
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        if ((fo.assignedLocation?.location?.id || '') !== locGid) continue;
        if (fo.isVirtualOnly) continue;
        if (fo._packedHere) continue;
        const dm = fo.deliveryMethod || {};
        // Parked at a pure retail pickup store = nothing to pack. At a franchise
        // store (backend flag false) the FO is assigned to its own pickup
        // location from the start and is still work (SP-341768, 2026-09-05).
        if (dm.methodType === 'PICK_UP' && fo._assignedIsRetailDest !== false) {
            const an = (fo.assignedLocation?.name || '').trim();
            const pn = (dm.presentedName || '').trim();
            if (an && pn && an === pn) continue;
        }
        for (const lie of (fo.lineItems?.edges || [])) {
            const n = lie.node || {};
            if (_isVirtualTitle(titleByOli[n.lineItem?.id])) continue;
            total += (n.remainingQuantity || 0);
        }
    }
    return total;
}

export default function QCActionBar({
    readyCount, reworkUnits, passedUnits, virtualFoIds = [],
    orderId, orderInfo, session, fetchFn, onSaveState, onDone,
}) {
    const t = useT();
    const [step, setStep]       = useState('idle');
    const [rateInfo, setRateInfo] = useState(null);
    const [error, setError]     = useState('');
    const [pdfUrl, setPdfUrl]   = useState(null);
    // Seconds counter for the "Purchasing label…" status — UPS label purchase
    // can take 5–15 s and the spinner alone makes it feel hung. Resets when
    // the step transitions away from 'buying'.
    const [buyingSec, setBuyingSec] = useState(0);
    useEffect(() => {
        if (step !== 'buying') { setBuyingSec(0); return; }
        setBuyingSec(0);
        const startedAt = Date.now();
        const id = setInterval(() => {
            setBuyingSec(Math.floor((Date.now() - startedAt) / 1000));
        }, 250);
        return () => clearInterval(id);
    }, [step]);
    // Raw PDF URL (combined label+slip) for the silent reprint iframe — set
    // after a successful shipping-label purchase. Pre-loading the PDF means
    // a Reprint tap fires window.print() inside the user-gesture handler with
    // ZERO async work, sidestepping Safari's "blocked from automatically
    // printing" prompt entirely. Different from pdfUrl (which is the HTML
    // wrapper opened in the popup).
    const [pdfRawUrl, setPdfRawUrl] = useState(null);
    const reprintPdfIframeRef = useRef(null);
    const [splitDone, setSplitDone] = useState(false);
    const [labelFoGid, setLabelFoGid] = useState(null);
    const [desktopPrintUrl, setDesktopPrintUrl] = useState(null);
    const [intlModalOpen, setIntlModalOpen] = useState(false);
    // 2026-09-11: customer paid for a non-ground service — slip only, no label.
    const [nonGroundModalOpen, setNonGroundModalOpen] = useState(false);
    // Partial-confirmation modal state (SP-297179 — replaces window.confirm()).
    // Holds {shipping, totalOpen, remaining, resolve} when awaiting the
    // operator's OK/Cancel; null otherwise. splitIfNeeded awaits the promise
    // that setPartialConfirm delivers back through `resolve`.
    const [partialConfirm, setPartialConfirm] = useState(null);
    const pollRef = useRef(null);
    const printIframeRef = useRef(null);
    const desktopIframeRef = useRef(null);
    const desktopPrintedRef = useRef(false);
    // iPad Safari tab (iPadOS 27): a popup can only open inside the tap, so the Fulfill / Confirm & Buy
    // tap opens a blank tab immediately and the purchased label document is sent into it when Shopify
    // returns; that document auto-prints itself exactly as the Home Screen app did before iOS 27.
    const ipadPrintWinRef = useRef(null);
    const openIpadPrintTab = () => {
        if (!IS_IPAD_SAFARI_TAB) return;
        try {
            if (ipadPrintWinRef.current && !ipadPrintWinRef.current.closed) return;
            ipadPrintWinRef.current = window.open('about:blank', '_blank');
        } catch (_) { ipadPrintWinRef.current = null; }
    };
    const closeIpadPrintTab = () => {
        try { if (ipadPrintWinRef.current && !ipadPrintWinRef.current.closed) ipadPrintWinRef.current.close(); } catch (_) {}
        ipadPrintWinRef.current = null;
    };
    const didAutoPrintRef = useRef(false);
    // Track whether the portal was in requestFullscreen mode when the desktop
    // print modal opened. Chrome force-exits fullscreen when window.print()
    // fires — that exit looks like a "new window popping up" (browser chrome
    // + taskbar reappear around the tab). We pre-emptively exit fullscreen
    // when opening the modal so the transition is smooth, then re-enter
    // fullscreen on close (user's click gesture satisfies the permission).
    const wasFullscreenRef = useRef(false);

    const openDesktopModal = (url) => {
        try {
            if (document.fullscreenElement) {
                wasFullscreenRef.current = true;
                document.exitFullscreen?.().catch(() => {});
            } else {
                wasFullscreenRef.current = false;
            }
        } catch (_) { wasFullscreenRef.current = false; }
        desktopPrintedRef.current = false;
        setDesktopPrintUrl(url);
    };
    const closeDesktopModal = () => {
        setDesktopPrintUrl(null);
        // The hidden "fallback" iframe watches pdfUrl and auto-fires print on
        // mount (iPad behavior). On desktop we never want that — we had the
        // modal. Wipe pdfUrl so the iframe doesn't silently re-mount and pop
        // another print dialog when the modal unmounts.
        setPdfUrl(null); setPdfRawUrl(null);
        // Re-enter fullscreen on the same user gesture that closed the modal.
        if (wasFullscreenRef.current) {
            wasFullscreenRef.current = false;
            try {
                document.documentElement.requestFullscreen?.().catch(() => {});
            } catch (_) { /* non-critical */ }
        }
    };

    useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

    // Listen for the buy-and-print popup signaling that the rate session
    // expired (Shopify persistently rejects the label_gid for DocumentCreate
    // — happens when the user takes too long between Get Rate and Confirm &
    // Buy Label, or after a stuck label was abandoned). Wipe the stale rate
    // info, drop the post-purchase pdfUrl link, and surface a clear message
    // so the operator knows to tap "Fulfill & Create Label" again to re-rate.
    useEffect(() => {
        const onMsg = (e) => {
            const data = e?.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'opshub:rate_expired') {
                setRateInfo(null);
                setPdfUrl(null); setPdfRawUrl(null);
                setStep('error');
                setError(
                    t('The shipping rate session expired before Shopify finished generating the label. No charge was completed. Tap Retry, then tap "Fulfill & Create Label" again to get a fresh rate.')
                );
                setDesktopPrintUrl(null);
            } else if (data.type === 'opshub:label_close') {
                // Close button on the print-preview HTML — tear down the
                // desktop in-page modal so the operator lands back on the
                // order. (Popup/iPad case: the print window already
                // closed itself; nothing to do here.)
                setDesktopPrintUrl(null);
            } else if (data.type === 'opshub:label_error') {
                // Generic label-creation failure (Shopify 404/500/etc on
                // ShippingLabelsPurchase, label prep crash, PDF timeout). The
                // popup/modal iframe already showed the error to the operator
                // with a Close button. Mirror it inline above the action bar
                // so they have something to clear AND a clear path back —
                // and close the desktop modal if it's still open.
                setRateInfo(null);
                setPdfUrl(null); setPdfRawUrl(null);
                const title = data.title || t('Label could not be created');
                const detail = data.detail || '';
                setError(
                    `${title}${detail ? '\n\n' + detail : ''}\n\n` +
                    t('No charge was completed. Tap Retry to start over.')
                );
                setStep('error');
                setDesktopPrintUrl(null);
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, []);

    const hasRework   = reworkUnits.length > 0;
    const hasPassed   = passedUnits.length > 0;
    const isPartial   = hasPassed && (readyCount > 0 || hasRework);

    const storeKey = session.store_key || '';
    const locGid   = `gid://shopify/Location/${session.shopify_location_id}`;
    // Slip-print endpoints credit the store_transfer timeline event to the
    // JWT user by default — but the JWT here is the kiosk admin (Eric), not
    // the PIN-portal operator (Mark). Forward the portal user as query
    // params so the backend can attribute the event correctly.
    const portalQ = session?.first_name
        ? `&portal_username=${encodeURIComponent(`${session.first_name} ${session.last_name || ''}`.trim())}`
          + (session.presser_id ? `&portal_userid=${encodeURIComponent(String(session.presser_id))}` : '')
        : '';
    const ownFo = orderInfo?.fulfillmentOrders?.edges
        ?.map(e => e.node)
        ?.find(fo => ['OPEN', 'IN_PROGRESS'].includes(fo.status)
            && !fo._packedHere
            && fo.assignedLocation?.location?.id === locGid);
    const foGid = ownFo?.id || '';
    // Treat as pickup if the FO delivery method is PICK_UP *or* if a store
    // pickup override has been set via Tools → Change Pickup Location.  The
    // backend injects `store_pickup_override` (string | undefined) into the
    // order object returned by GET /api/packing/orders/{id}.
    const isPickup = ownFo?.deliveryMethod?.methodType === 'PICK_UP'
        || !!orderInfo?.store_pickup_override;
    // 2026-07-28: Bulk Club Delivery — orders shipped in bulk to a club
    // coordinator's warehouse (not a per-customer pickup and not a UPS
    // shipment). Detect via the FO's shipping-line presentedName and
    // route the QC action to a single "Print Bulk Delivery Slip" button —
    // no rate lookup, no UPS label purchase, no pickup transfer. Backend
    // packing-slip-print stamps the top-left header as "BULK DELIVERY"
    // when it sees the same presentedName so operators can tell the
    // resulting slip apart from a store-branded pickup slip at a glance.
    const isBulkClubDelivery = (
        (ownFo?.deliveryMethod?.presentedName || '').trim().toLowerCase()
    ) === 'bulk club delivery';

    // International detection. Shopify's shippingAddress.countryCode is the
    // ISO 3166-1 alpha-2 code, but for US-territory addresses (Puerto Rico,
    // Guam, USVI, etc.) Shopify reports countryCode='US' with the territory
    // in provinceCode (e.g. PR). UPS treats these as international shipments
    // — customs forms required, no domestic UPS label flow — so we have to
    // gate on BOTH a non-US countryCode AND on these specific US-territory
    // province codes. Block the automated rate/purchase flow and surface a
    // modal directing the operator to ship via Shopify admin; packing slip
    // still prints.
    // 2026-09-18 (Eric): Puerto Rico left this list. Shopify quotes UPS Ground
    // to PR through the portal's own rate path, and the print page now carries
    // Shopify's customs form (3 copies) between the label and the slip — so PR
    // buys here like a domestic order, but always behind the confirm gate.
    const US_TERRITORIES = ['VI', 'GU', 'AS', 'MP'];  // USVI, Guam, Samoa, N. Marianas
    const shippingCountry  = (orderInfo?.shippingAddress?.countryCode  || '').toUpperCase();
    const shippingProvince = (orderInfo?.shippingAddress?.provinceCode || '').toUpperCase();
    const isPuertoRico = shippingCountry === 'US' && shippingProvince === 'PR';
    const isInternational =
        (shippingCountry !== '' && shippingCountry !== 'US')
        || (shippingCountry === 'US' && US_TERRITORIES.includes(shippingProvince));
    // Pretty label for the modal: 'PR' / 'GU' / 'CA' rather than country=US for PR.
    const intlDestinationLabel = shippingCountry === 'US' ? shippingProvince : shippingCountry;

    // 2026-09-11: the portal only ever buys UPS Ground (1 lb). When the
    // customer paid for something else (Priority Mail, 2nd Day Air, …) the
    // order still splits and the slip still prints, but the label step is
    // replaced by a "ship via Shopify" instruction. The list lives in
    // constants/groundShippingMethods.js. Pickup, Bulk Club Delivery and
    // international orders have their own flows and take precedence.
    const paidShippingTitle = (orderInfo?.shippingLine?.title || '').trim();
    const isNonGroundShipping = !isGroundShippingTitle(paidShippingTitle);

    // 2026-09-11: Canada. When the customer paid for UPS Standard and the
    // address is CA, the portal buys UPS Standard (service 11) itself instead
    // of sending the operator to Shopify admin — same rate → confirm → buy
    // flow as Ground, but NEVER auto-approved: the price is shown and the
    // operator confirms. Customs go out from the line prices (no HS codes),
    // duties are collected from the recipient. The backend refuses any
    // other country/service pairing.
    const isCanadaUpsStandard = shippingCountry === 'CA'
        && normalizeShippingTitle(paidShippingTitle) === 'ups standard';

    // Detect orders already completed elsewhere: no OPEN FO at this location,
    // but the order has at least one non-CLOSED FO somewhere (pickup order already
    // transferred to a store) or no active FOs at all (shipping order already shipped).
    const allFos = (orderInfo?.fulfillmentOrders?.edges || []).map(e => e.node);
    const hasAnyOpenFoAtThisLocation = !!ownFo;
    const hasFoElsewhere = allFos.some(fo =>
        ['OPEN', 'IN_PROGRESS'].includes(fo.status)
        && fo.assignedLocation?.location?.id !== locGid
    );
    const allFosClosed = allFos.length > 0 && allFos.every(fo => fo.status === 'CLOSED');
    // ALSO consider an order transferred when its only open FO is a pickup FO
    // already parked at its pickup destination (assignedLocation == presentedName).
    // This happens when the user opens via sidebar deep-link: the per-order
    // session auto-resolves locGid to the pickup location, so `ownFo` is truthy
    // even though the order's already at the store. Without this branch we'd
    // hit the "has work to do" path and the operator never sees a clear
    // "already transferred" message.
    const openFosForOrder = allFos.filter(fo => ['OPEN', 'IN_PROGRESS'].includes(fo.status));
    const allOpenAreParkedPickup = openFosForOrder.length > 0 && openFosForOrder.every(fo => {
        // Slip already printed here → nothing left on this FO.
        if (fo._packedHere) return true;
        const dm = fo.deliveryMethod || {};
        if (dm.methodType !== 'PICK_UP') return false;
        // Franchise stores fulfill their own pickups: never "parked" there.
        if (fo._assignedIsRetailDest === false) return false;
        const assignedName  = (fo.assignedLocation?.name || '').trim();
        const presentedName = (dm.presentedName || '').trim();
        return assignedName && presentedName && assignedName === presentedName;
    });
    const transferredMsg = t('This order has already been transferred to its pickup location. No action available here.');
    // Franchise: the packed FO stays at this location after the slip prints
    // (no physical move), flagged _packedHere by the backend.
    const packedHere = allFos.some(fo => fo._packedHere && ['OPEN', 'IN_PROGRESS'].includes(fo.status));
    const alreadyDoneMessage = !hasAnyOpenFoAtThisLocation
        ? ((hasFoElsewhere || packedHere)
            ? transferredMsg
            : (allFosClosed ? t('This order has already been fulfilled. No action available.') : null))
        : (allOpenAreParkedPickup ? transferredMsg : null);

    const passedFoAgg = {};
    for (const u of passedUnits) {
        const refs = u.foLineItemRefs || u.foLineItemIds.map(id => ({ id, orderLineItemId: null }));
        for (const ref of refs) {
            if (!passedFoAgg[ref.id]) {
                passedFoAgg[ref.id] = { id: ref.id, quantity: 0, order_line_item_id: ref.orderLineItemId || null };
            }
            passedFoAgg[ref.id].quantity += 1;
        }
    }
    const passedFoLineItems = Object.values(passedFoAgg);

    // SP-297179 under-scan guard.
    //   totalOpenUnits    — bundles+base items+embellishments on OPEN FOs at this loc
    //   passedUnitCount   — units the operator actually QC'd as pass (from passedFoLineItems)
    //   clientIsPartial   — FE's belief that this isn't a complete pack of the FO
    // The QCActionBar flow is always operator-driven (they clicked
    // "Buy Label"/"Print Slip"), and isPartial is derived from
    // hasPassed && (readyCount>0 || hasRework). We send acknowledge_partial
    // whenever the FE thinks it's partial, so the operator's intent is on
    // record. Backend 409s only when server view says partial and we didn't
    // acknowledge — that means the FE undercounted (stale snapshot / filter
    // bug), so we surface a native confirm and retry with ack=true.
    const totalOpenUnitsAtLoc = computeTotalOpenUnitsAtLoc(orderInfo, session?.shopify_location_id);
    const passedUnitCount = passedFoLineItems.reduce((s, li) => s + (li.quantity || 0), 0);
    const clientIsPartial = totalOpenUnitsAtLoc > 0 && passedUnitCount < totalOpenUnitsAtLoc;

    const postComplete = async (ackPartial) => {
        return await fetchFn(`/api/packing/orders/${orderId}/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                order_name:              orderInfo.name,
                shopify_location_id:     session.shopify_location_id,
                portal_userid:           session.presser_id,
                portal_username:         `${session.first_name} ${session.last_name}`,
                presser_id:              session.presser_id,
                passed_fo_line_items:    passedFoLineItems,
                fulfillment_order_id:    foGid,
                client_is_partial:       clientIsPartial || isPartial,
                client_total_open_units: totalOpenUnitsAtLoc,
                acknowledge_partial:     ackPartial,
            }),
        }, storeKey);
    };

    // H1: fetch a short-lived, label-only token to put in browser-opened print
    // URLs, so the full 30-day session JWT never lands in the URL / nginx logs /
    // browser history. Cached ~12 min (server TTL is 15). Falls back to the
    // session token if the mint call fails, so printing degrades gracefully
    // rather than breaking the kiosk.
    const labelTokRef = useRef({ tok: '', exp: 0 });
    const getLabelToken = async () => {
        const now = Date.now();
        if (labelTokRef.current.tok && now < labelTokRef.current.exp) return labelTokRef.current.tok;
        const sessionJwt = localStorage.getItem('operations_token')
                        || sessionStorage.getItem('pressing_admin_token')
                        || localStorage.getItem('ops_portal_token') || '';
        try {
            const r = await fetchFn('/api/label-token', { method: 'POST' }, storeKey);
            if (r && r.ok) {
                const d = await r.json();
                if (d && d.token) {
                    labelTokRef.current = { tok: d.token, exp: now + 12 * 60 * 1000 };
                    return d.token;
                }
            }
        } catch (_) { /* fall through to session token */ }
        return sessionJwt;
    };

    const splitIfNeeded = async () => {
        let effectiveFoGid = labelFoGid || foGid;
        // Belt & suspenders (SP-297179): whenever this flow is about to fire a
        // partial fulfillment — either because the operator's QC state has
        // ready/rework items left (isPartial) OR because the fresh per-location
        // count says the passed subset doesn't cover every open unit
        // (clientIsPartial) — surface a modal-style confirm BEFORE calling
        // /complete. The explicit "Partially Fulfill Order" button label was
        // not enough on its own: on the packing floor, operators can trigger
        // a partial by mis-scanning or by tapping through fast, and the split
        // is irreversible. Modal ties intent to the actual count they're
        // shipping vs. what's still open.
        const willBePartial = (isPartial || clientIsPartial) && !splitDone;
        if (willBePartial) {
            // Modal is OPERATOR-facing → count in cards/bundles (what they
            // scan and see on the kanban), NOT FO line items. A jersey bundle
            // = 1 card even though it expands to parent + embellishments in
            // Shopify's FO. passedUnits.length is the passed-card count;
            // readyCount is the remaining-to-scan card count.
            const shippingCards = passedUnits.length;
            const readyCards    = readyCount || 0;
            const totalCards    = shippingCards + readyCards;
            const remainingCards = Math.max(0, totalCards - shippingCards);
            // Await the in-app modal. setPartialConfirm hands the resolver to
            // the modal's OK/Cancel buttons; they call resolve(true|false) and
            // clear the state.
            const proceed = await new Promise((resolve) => {
                setPartialConfirm({
                    shipping:  shippingCards,
                    totalOpen: totalCards,
                    remaining: remainingCards,
                    resolve,
                });
            });
            if (!proceed) throw new Error('Cancelled by operator');
            let r = await postComplete(true);
            let d = await r.json();
            if (r.status === 409 && d?.detail?.code === 'under_scan') {
                // Backend disagreed even with ack=true — shouldn't happen but
                // surface cleanly.
                throw new Error(d.detail.message || 'Under-scan blocked by server');
            }
            if (!r.ok) throw new Error((d.detail && (d.detail.message || d.detail)) || 'Split failed');
            setSplitDone(true);
            if (d.replacement_fo_id) effectiveFoGid = d.replacement_fo_id;
        }
        setLabelFoGid(effectiveFoGid);
        return effectiveFoGid;
    };

    // Non-pickup flow: split → rate → purchase → print combined PDF.
    //
    // NOTE: virtual-FO auto-fulfill (qty-0 NONE-method placeholder FOs) was
    // previously fired BEFORE the rate fetch, but Shopify's mutation pipeline
    // takes 3-5s to settle. A label session created within that window can be
    // invalidated server-side, causing ShippingLabelsPurchase to 404 (seen on
    // SP-248877 2026-05-05 11:25). Fix: defer the virtual-FO fulfill until
    // AFTER the label is purchased (see purchaseLabel below). Virtual FOs are
    // independent of the shipping FO, so deferring is safe.
    const splitAndLabel = async (opts = {}) => {
        const serviceCode = (opts && opts.serviceCode) || '03';
        openIpadPrintTab();   // must happen inside the tap (iPad Safari)
        setStep('splitting'); setError('');
        try {
            await onSaveState?.();
            const effectiveFoGid = await splitIfNeeded();
            setStep('getting-rate');
            const rateRes = await fetchFn(`/api/orders/${orderId}/shipping-label/rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_id: effectiveFoGid, service_code: serviceCode }),
            }, storeKey);
            const rateData = await rateRes.json();
            if (!rateRes.ok) throw new Error(rateData.detail || 'Rate fetch failed');
            setRateInfo(rateData);
            // Auto-approve UPS Ground (2026-08-23): the rate endpoint only
            // ever returns UPS Ground (ups_shipping/03) or the USPS
            // Ground Advantage PO-box fallback. UPS Ground at a sane price
            // needs no operator confirmation — skip the "Confirm & Buy
            // Label" tap and purchase immediately (price still shown in
            // the Purchasing status + on the label page). The confirm gate
            // remains for the USPS fallback and for any quote above the
            // ceiling — a wild price (weight/dimension bug) should get
            // eyes before money moves.
            const _price = parseFloat(rateData.quoted_price);
            const _isUpsGround = serviceCode === '03'
                && (rateData.carrier_code || 'ups_shipping') === 'ups_shipping'
                && (rateData.service_code || '03') === '03'
                && !rateData.fallback_reason;
            if (_isUpsGround && !opts.requireConfirm && Number.isFinite(_price) && _price > 0
                    && _price <= AUTO_BUY_MAX_USD) {
                await purchaseLabel(rateData, effectiveFoGid);
                return;
            }
            setStep('rate');
        } catch (e) { closeIpadPrintTab(); setError(e.message); setStep('error'); }
    };

    // Pickup flow: split (if partial) → open HTML packing slip in new window.
    // Backend does the Shopify transfer; HTML auto-triggers AirPrint on load.
    // Bulk Club Delivery: after the bulk slip prints, mark the FO fulfilled in
    // Shopify with tracking "BULK" / carrier "Other" and NO customer
    // notification — mirrors the manual Mark-as-fulfilled the ops team used to
    // do by hand. Reuses the existing /fulfill endpoint (notify_customer=false
    // is passed explicitly, not just defaulted). Throws on failure so the
    // operator sees the slip printed but the FO was NOT fulfilled and can retry
    // from Orders.
    const markBulkFulfilled = async (foGid) => {
        const sk = storeKey || 'soccerpost';
        const r = await fetchFn(`/api/orders/${orderId}/fulfill`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                fulfillment_order_ids: [foGid],
                tracking_number:       'BULK',
                tracking_company:      'Other',
                notify_customer:       false,
            }),
        }, sk);
        if (!r || !r.ok) {
            let msg = 'Shopify fulfillment failed';
            try { const d = await r.json(); msg = (d.detail && (d.detail.message || d.detail)) || msg; } catch (_) {}
            throw new Error(`Bulk slip printed, but marking the FO fulfilled failed: ${msg}. Retry from Orders.`);
        }
    };

    const splitAndSlip = async (opts = {}) => {
        const fulfillBulk = opts && opts.fulfillBulk === true;
        setStep('splitting'); setError('');
        // Desktop: skip popup entirely, render in an in-page modal iframe so
        // fullscreen portal mode isn't interrupted by a focus-stealing window.
        if (IS_DESKTOP_PRINT) {
            try {
                await onSaveState?.();
                const effectiveFoGid = await splitIfNeeded();
                setStep('getting-slip');
                const jwt = await getLabelToken();  // H1: label-only short-lived token, not the session JWT
                const sk = storeKey || 'soccerpost';
                const url = `/api/orders/${orderId}/packing-slip-print`
                    + `?fulfillment_order_id=${encodeURIComponent(effectiveFoGid)}`
                    + `&store=${encodeURIComponent(sk)}`
                    + `&token=${encodeURIComponent(jwt)}${portalQ}`;
                // Don't setPdfUrl on desktop — the hidden fallback iframe
                // would re-mount + auto-print the moment the modal closes.
                openDesktopModal(`${url}&desktop=1`);
                if (fulfillBulk) await markBulkFulfilled(effectiveFoGid);
                setStep('pickup-done');
            } catch (e) { setError(e.message); setStep('error'); }
            return;
        }
        // iPad / mobile: original flow — unchanged.
        const printWin = window.open('about:blank', '_blank');
        try {
            await onSaveState?.();
            const effectiveFoGid = await splitIfNeeded();
            setStep('getting-slip');
            // PDF variant: the PDF has an embedded 4×4 page size, which iOS's PDF viewer
            // honors more consistently than HTML @page rules. Navigate the popup to the
            // real URL so iOS can persist AirPrint settings per-document.
            const jwt = await getLabelToken();  // H1: label-only short-lived token, not the session JWT
            const sk = storeKey || 'soccerpost';
            const url = `/api/orders/${orderId}/packing-slip-print`
                + `?fulfillment_order_id=${encodeURIComponent(effectiveFoGid)}`
                + `&store=${encodeURIComponent(sk)}`
                + `&token=${encodeURIComponent(jwt)}${portalQ}`;
            if (printWin && !printWin.closed) {
                printWin.location.href = url;
            }
            setPdfUrl(url);
            if (fulfillBulk) await markBulkFulfilled(effectiveFoGid);
            setStep('pickup-done');
        } catch (e) {
            try { printWin?.close?.(); } catch (_) {}
            setError(e.message); setStep('error');
        }
    };

    // International shipping: split (if partial) → render packing slip only.
    // Backend endpoint /packing-slip-print is reused — for non-pickup orders
    // it skips the FO transfer block, returning just the printable HTML slip.
    // No UPS rate fetch, no label purchase, no Shopify fulfillment. Operator
    // ships from Shopify admin manually.
    // Non-ground shipping method: same shape as the international flow —
    // split (if partial) → packing slip only, tagged non_ground=1 so the
    // backend stamps SHIP VIA SHOPIFY + the paid service on the slip.
    const splitAndNonGroundSlip = async () => {
        setStep('splitting'); setError('');
        const buildUrl = (effectiveFoGid, jwt, sk) =>
            `/api/orders/${orderId}/packing-slip-print`
            + `?fulfillment_order_id=${encodeURIComponent(effectiveFoGid)}`
            + `&store=${encodeURIComponent(sk)}`
            + `&non_ground=1`
            + `&token=${encodeURIComponent(jwt)}${portalQ}`;
        if (IS_DESKTOP_PRINT) {
            try {
                await onSaveState?.();
                const effectiveFoGid = await splitIfNeeded();
                setStep('getting-slip');
                const jwt = await getLabelToken();
                const url = buildUrl(effectiveFoGid, jwt, storeKey || 'soccerpost');
                openDesktopModal(`${url}&desktop=1`);
                setStep('nonground-done');
            } catch (e) { setError(e.message); setStep('error'); }
            return;
        }
        const printWin = window.open('about:blank', '_blank');
        try {
            await onSaveState?.();
            const effectiveFoGid = await splitIfNeeded();
            setStep('getting-slip');
            const jwt = await getLabelToken();
            const url = buildUrl(effectiveFoGid, jwt, storeKey || 'soccerpost');
            if (printWin && !printWin.closed) {
                printWin.location.href = url;
            }
            setPdfUrl(url);
            setStep('nonground-done');
        } catch (e) {
            try { printWin?.close?.(); } catch (_) {}
            setError(e.message); setStep('error');
        }
    };

    const splitAndIntlSlip = async () => {
        setStep('splitting'); setError('');
        if (IS_DESKTOP_PRINT) {
            try {
                await onSaveState?.();
                const effectiveFoGid = await splitIfNeeded();
                setStep('getting-slip');
                const jwt = await getLabelToken();  // H1: label-only short-lived token, not the session JWT
                const sk = storeKey || 'soccerpost';
                const url = `/api/orders/${orderId}/packing-slip-print`
                    + `?fulfillment_order_id=${encodeURIComponent(effectiveFoGid)}`
                    + `&store=${encodeURIComponent(sk)}`
                    + `&token=${encodeURIComponent(jwt)}${portalQ}`;
                openDesktopModal(`${url}&desktop=1`);
                setStep('intl-done');
            } catch (e) { setError(e.message); setStep('error'); }
            return;
        }
        const printWin = window.open('about:blank', '_blank');
        try {
            await onSaveState?.();
            const effectiveFoGid = await splitIfNeeded();
            setStep('getting-slip');
            const jwt = await getLabelToken();  // H1: label-only short-lived token, not the session JWT
            const sk = storeKey || 'soccerpost';
            const url = `/api/orders/${orderId}/packing-slip-print`
                + `?fulfillment_order_id=${encodeURIComponent(effectiveFoGid)}`
                + `&store=${encodeURIComponent(sk)}`
                + `&token=${encodeURIComponent(jwt)}${portalQ}`;
            if (printWin && !printWin.closed) {
                printWin.location.href = url;
            }
            setPdfUrl(url);
            setStep('intl-done');
        } catch (e) {
            try { printWin?.close?.(); } catch (_) {}
            setError(e.message); setStep('error');
        }
    };

    // `rateOverride`: the auto-approve path calls this immediately after
    // setRateInfo() — React state hasn't committed yet, so the fresh rate
    // payload is passed directly. Manual "Confirm & Buy Label" taps omit it
    // and fall back to the committed rateInfo state.
    const purchaseLabel = async (rateOverride = null, foGidOverride = null) => {
        const ri = rateOverride || rateInfo;
        // H8: in the auto-buy path purchaseLabel fires in the same tick as the
        // split, so the labelFoGid state hasn't committed yet — read the fresh
        // FO gid passed by the caller first, then the settled state, then the
        // original. Without this, the label is recorded against the pre-split FO.
        const effectiveFoGid = foGidOverride || labelFoGid || foGid;
        openIpadPrintTab();   // no-op unless iPad Safari; also no-op when the Fulfill tap already opened it
        setStep('buying'); setError('');
        const sk = storeKey || 'soccerpost';
        const jwt = await getLabelToken();  // H1: label-only short-lived token, not the session JWT
        // Auto-fulfill any virtual placeholder FOs *now* (deferred from the
        // earlier rate step). Fire-and-forget — it doesn't block the label
        // purchase, and even if it 500s the operator can re-run from Orders.
        if (virtualFoIds.length > 0) {
            fetchFn(`/api/orders/${orderId}/fulfill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fulfillment_order_ids: virtualFoIds }),
            }, sk).catch(() => {});
        }

        // Desktop: iframe-in-modal avoids the focus-stealing new-window popup
        // that breaks fullscreen mode. Use the existing single-call streaming
        // endpoint here — desktop tabs don't have iOS Safari's auto-print
        // user-activation problem, so there's no need for the foreground-
        // purchase split.
        if (IS_DESKTOP_PRINT) {
            const q = new URLSearchParams({
                fulfillment_order_id: effectiveFoGid,
                label_gid:            ri.label_gid,
                session_gid:          ri.session_gid,
                quoted_price:         String(ri.quoted_price),
                shipping_date:        String(ri.shipping_date),
                portal_presser_id:    String(session.presser_id),
                portal_presser_name:  `${session.first_name} ${session.last_name}`,
                portal_type:          'packing',
                store:                sk,
                token:                jwt,
                desktop:              '1',
            });
            if (ri.carrier_code) q.set('carrier_code', ri.carrier_code);
            if (ri.service_code) q.set('service_code', ri.service_code);
            const desktopUrl = `/api/orders/${orderId}/shipping-label/buy-and-print-stream?${q.toString()}`;
            openDesktopModal(desktopUrl);
            setStep('done');
            return;
        }

        // iPad / mobile path: in-page state only — no popup. In standalone
        // PWA mode, window.open() opens the popup in regular Safari (outside
        // the PWA window), breaking user-activation chain and triggering the
        // "blocked from automatically printing" prompt. Instead, we show the
        // in-page "Purchasing label…" spinner from the 'buying' step, and
        // when the purchase completes the 'done' step renders a Reprint
        // Label button that fires window.print() synchronously inside its
        // click handler — guaranteed user-gesture, no Allow prompt.
        try {
            const r = await fetchFn(`/api/orders/${orderId}/shipping-label/purchase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fulfillment_order_id: effectiveFoGid,
                    label_gid:            ri.label_gid,
                    session_gid:          ri.session_gid,
                    quoted_price:         String(ri.quoted_price),
                    shipping_date:        String(ri.shipping_date),
                    portal_presser_id:    Number(session.presser_id),
                    portal_presser_name:  `${session.first_name} ${session.last_name}`,
                    portal_type:          'packing',
                    // Carry USPS Ground Advantage through when the rate
                    // endpoint fell back off UPS (PO-Box addresses).
                    carrier_code:         ri.carrier_code || 'ups_shipping',
                    service_code:         ri.service_code || '03',
                }),
            }, sk);
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j?.detail || `Purchase failed (HTTP ${r.status})`);
            }
            const data = await r.json();
            const labelTail = String(data.label_gid).split('/').pop();
            const renderUrl = `/api/shipping-labels/${labelTail}/combined-html?store=${encodeURIComponent(sk)}&token=${encodeURIComponent(jwt)}${portalQ}`;
            const rawPdfUrl = `/api/shipping-labels/${labelTail}/combined-pdf?store=${encodeURIComponent(sk)}&token=${encodeURIComponent(jwt)}${portalQ}`;
            setPdfUrl(renderUrl);
            setPdfRawUrl(rawPdfUrl);
            setStep('done');
            if (IS_IPAD_SAFARI_TAB) {
                const w = ipadPrintWinRef.current;
                ipadPrintWinRef.current = null;
                try {
                    if (w && !w.closed) w.location.href = renderUrl;   // the document auto-prints itself
                    else window.open(renderUrl, '', 'width=920,height=1100,scrollbars=yes,resizable=yes');
                } catch (_) {}
            }
        } catch (e) {
            closeIpadPrintTab();
            setError(e?.message || 'Failed to buy shipping label.');
            setStep('error');
        }
    };

    // Print the pre-loaded label iframe in the current window. Toggles a
    // body class that activates the @media print rule which hides every
    // parent-app element and shows ONLY the .qcb-print-iframe-prerendered
    // iframe, then synchronously fires window.print() inside the click's
    // user-activation window.
    //
    // Critical iPad-PWA detail: iPad Safari running as a standalone PWA
    // grants the FIRST window.print() in a page lifecycle without a prompt,
    // but every subsequent call shows "This website has been blocked from
    // automatically printing" — the print engine treats the page as already
    // having printed. The fix is to cache-bust the iframe's src right
    // before calling print(); iPad sees a fresh navigation and resets the
    // print state, so the second/third/Nth print is treated like the first.
    const onParentPrintClick = () => {
        if (!pdfUrl) return;
        if (IS_STANDALONE_PWA) {
            // Standalone PWA: navigate the parent window to the print URL.
            // Each navigation creates a fresh document → auto-print fires
            // as "first print of new lifecycle" → no Allow prompt.
            window.location.href = buildPwaPrintUrl(pdfUrl, orderId, storeKey, session?.shopify_location_id);
            return;
        }
        if (IS_IPAD_SAFARI_TAB) {
            // iPad Safari tab: iOS prints iframes blank — open the unchanged label + slip document in its
            // own tab from this tap; it auto-prints itself exactly as the Home Screen app printed it before iOS 27.
            const u = pdfUrl.replace(/[?&]desktop=1/, '');
            try { window.open(u, '', 'width=920,height=1100,scrollbars=yes,resizable=yes'); } catch (_) {}
            return;
        }
        // Non-PWA (desktop, in-browser iPad): parent window.print() with
        // @media print rule that shows only the pre-loaded iframe content.
        document.body.classList.add('qcb-print-iframe-mode');
        try { window.print(); } catch (_) {}
        const off = () => {
            document.body.classList.remove('qcb-print-iframe-mode');
            window.removeEventListener('afterprint', off);
        };
        window.addEventListener('afterprint', off);
        setTimeout(off, 5000);
    };

    // Auto-fire the print dialog the first time a fresh pdfUrl is set
    // (right after a successful label purchase). In PWA, navigate to the
    // print URL — same flow as a manual Reprint tap. In non-PWA, fire the
    // parent window.print() directly.
    const firstAutoPrintRef = useRef(false);
    useEffect(() => {
        if (!pdfUrl || firstAutoPrintRef.current) return;
        firstAutoPrintRef.current = true;
        if (IS_IPAD_SAFARI_TAB) return;   // no automatic print on an iPad Safari tab: the operator taps Reprint Label → PDF tab
        if (IS_STANDALONE_PWA) {
            window.location.href = buildPwaPrintUrl(pdfUrl, orderId, storeKey, session?.shopify_location_id);
            return;
        }
        const timer = setTimeout(() => {
            document.body.classList.add('qcb-print-iframe-mode');
            try { window.print(); } catch (_) {}
            setTimeout(() => document.body.classList.remove('qcb-print-iframe-mode'), 5000);
        }, 600);
        return () => clearTimeout(timer);
    }, [pdfUrl]);  // eslint-disable-line react-hooks/exhaustive-deps

    const sendForRework = () => { onDone('rework'); };

    // If the order is already transferred / already fulfilled, surface a
    // disabled state instead of the action buttons so the user can't
    // re-fulfill — but ALSO offer a Reprint Label button when a stored
    // shipping_labels row exists, so operators who reopen a shipped order
    // can recover the label without going to Order Detail.
    if (alreadyDoneMessage) {
        const isShipped = allFosClosed || (orderInfo?.displayFulfillmentStatus === 'FULFILLED');
        // For pickup orders, find a relevant pickup FO so AlreadyDoneBar can
        // render a "Reprint Packing Slip" button (instead of falling back to
        // a Shopify-Admin deep-link that's only useful for shipping labels).
        // Prefer the FO at the operator's current location, else any pickup
        // FO on the order.
        const pickupFo = allFos.find(fo =>
            fo.deliveryMethod?.methodType === 'PICK_UP'
            && fo.assignedLocation?.location?.id === locGid
        ) || allFos.find(fo => fo.deliveryMethod?.methodType === 'PICK_UP');
        // Bulk Club Delivery: detect status-independently (ownFo/isBulkClubDelivery
        // above only see the OPEN FO, which is null once fulfilled). A fulfilled
        // bulk order has no shipping label and isn't a pickup, so without this it
        // would show no Reprint button at all. Find the bulk FO by presentedName,
        // preferring the one at the operator's location, so AlreadyDoneBar can
        // offer "Reprint Bulk Delivery Slip".
        const _isBulkFo = fo => ((fo.deliveryMethod?.presentedName || '').trim().toLowerCase() === 'bulk club delivery');
        const bulkFo = allFos.find(fo => _isBulkFo(fo) && fo.assignedLocation?.location?.id === locGid)
            || allFos.find(_isBulkFo);
        return (
            <AlreadyDoneBar
                message={alreadyDoneMessage}
                orderId={orderId}
                storeKey={storeKey}
                shopifyLocationId={session?.shopify_location_id}
                fetchFn={fetchFn}
                isShipped={isShipped}
                isPickup={isPickup || !!pickupFo}
                pickupFoGid={pickupFo?.id || ''}
                bulkFoGid={bulkFo?.id || ''}
                t={t}
            />
        );
    }

    // 2026-08-27: partially-fulfilled orders — the already-shipped portion's
    // label/slip must be reprintable from the packing portal too (user
    // request, SP-318438 canonical: two CLOSED shipping FOs + one OPEN with
    // no QC activity yet — the bar rendered nothing at all, and reprint
    // required a trip to Order Detail).
    // Virtual-only FOs (fee/handling placeholders) never count as a
    // fulfilled portion (2026-08-27c).
    const _closedShippedFo = allFos.find(fo =>
        fo.status === 'CLOSED' && !fo.isVirtualOnly
        && fo.deliveryMethod?.methodType === 'SHIPPING');
    const _closedPickupFo = allFos.find(fo =>
        fo.status === 'CLOSED' && !fo.isVirtualOnly
        && fo.deliveryMethod?.methodType === 'PICK_UP');
    // 2026-08-27c: only on orders Shopify itself reports PARTIALLY_FULFILLED
    // — a CLOSED FO alone can be a voided label (CLOSED, never fulfilled),
    // and the row must never appear when nothing has actually shipped.
    const _hasFulfilledPortion = (orderInfo?.displayFulfillmentStatus === 'PARTIALLY_FULFILLED')
        && !!(_closedShippedFo || _closedPickupFo);
    const _fulfilledPortionBar = _hasFulfilledPortion ? (
        <AlreadyDoneBar
            message={t('Previously fulfilled portion of this order:')}
            orderId={orderId}
            storeKey={storeKey}
            shopifyLocationId={session?.shopify_location_id}
            fetchFn={fetchFn}
            isShipped={!!_closedShippedFo}
            isPickup={!!_closedPickupFo && !_closedShippedFo}
            pickupFoGid={_closedPickupFo?.id || ''}
            bulkFoGid={''}
            t={t}
        />
    ) : null;

    // Hide action bar unless there's something passed OR only rework (previous
    // behavior) — but still surface the fulfilled-portion reprint row.
    if (!hasPassed && !hasRework) return _fulfilledPortionBar;

    // Defensive gate: a SHIPPING order without a real shipping address can't
    // produce a UPS label — Shopify's CreateShippingLabel mutation returns
    // userErrors and the operator gets a confusing toast. Detect the bad
    // state up front so the button is replaced with a clear hint. Common
    // origin: pickup-style orders that were accidentally flipped to SHIPPING
    // method (shippingLine.title is a store name, no shippingAddress).
    const noShipAddress = !orderInfo?.shippingAddress;
    const shippingTitle = (orderInfo?.shippingLine?.title || '').trim();
    const looksLikePickupRate = /^Soccer Post |^Teams /i.test(shippingTitle);
    // Bulk Club Delivery bypasses the shipping-guard block below — it never
    // buys a UPS label so a missing address or pickup-shaped rate title
    // shouldn't hard-fail the operator into "Can't buy a UPS label" copy.
    const cantShip = !isPickup && !isBulkClubDelivery && (noShipAddress || looksLikePickupRate);
    if (cantShip) {
        return (
            <div className="qcb-action-bar">
                <div className="qcb-action-bar__status qcb-action-bar__status--done"
                     style={{ background: '#fee2e2', color: '#991b1b' }}>
                    {noShipAddress
                        ? t("Can't buy a UPS label — this order has no shipping address. Add an address in Shopify admin or change the pickup location via Tools → Change Pickup Location.")
                        : t("Can't buy a UPS label — the shipping rate looks like a pickup-store name (\"") + shippingTitle + t("\"). Convert to a pickup order via Tools → Change Pickup Location.")}
                </div>
            </div>
        );
    }

    // For international shipping orders we route the click through a modal
    // that explains the constraint; the modal's primary action then triggers
    // splitAndIntlSlip. Pickup flow takes precedence (a US-store pickup of
    // an international customer is still a pickup) and is unaffected.
    // Bulk Club Delivery is a third flow — reuses splitAndSlip (the same
    // packing-slip-print target the pickup flow hits) but with a distinct
    // button label so the operator knows they're printing a bulk-delivery
    // slip, not a store-branded pickup slip. Bulk takes priority over the
    // shipping/international branches (it's neither a pickup nor a UPS
    // shipment) and never buys a UPS label.
    const primaryOnClick = isBulkClubDelivery
        ? (() => splitAndSlip({ fulfillBulk: true }))
        : (isPickup
            ? splitAndSlip
            : (isCanadaUpsStandard ? () => splitAndLabel({ serviceCode: '11' })
                : (isPuertoRico ? () => splitAndLabel({ serviceCode: '03', requireConfirm: true })
                : (isInternational ? () => setIntlModalOpen(true)
                    : (isNonGroundShipping ? () => setNonGroundModalOpen(true) : splitAndLabel)))));
    const primaryLabel = isBulkClubDelivery
        ? (isPartial ? t('Print Bulk Delivery Slip (Partial)')
                     : t('Print Bulk Delivery Slip'))
        : (isPartial
            ? (isPickup ? t('Create Label & Transfer To Pickup Location (Partial)')
                        : t('Partially Fulfill Order'))
            : (isPickup ? t('Create Label & Transfer To Pickup Location')
                        : t('Fulfill & Create Label')));
    const primaryClass = isPartial ? 'qcb-action-btn qcb-action-btn--orange' : 'qcb-action-btn qcb-action-btn--green';

    return (
        <>
        <div className="qcb-action-bar">
            {step === 'idle' && (
                <div className="qcb-action-bar__buttons">
                    {hasPassed && (
                        <button className={primaryClass} onClick={primaryOnClick}>
                            {primaryLabel}
                        </button>
                    )}
                    {hasRework && !hasPassed && (
                        <button className="qcb-action-btn qcb-action-btn--amber" onClick={sendForRework}>
                            {t('Send for Rework')} ({reworkUnits.length})
                        </button>
                    )}
                </div>
            )}
            {/* Partial orders: reprint the already-shipped portion's label(s)
                without leaving the QC flow — rendered INSIDE this bar so it
                can never overlap the primary button (SP-340254 lesson). */}
            {step === 'idle' && _hasFulfilledPortion && (
                <InlineLabelReprints
                    orderId={orderId}
                    storeKey={storeKey}
                    shopifyLocationId={session?.shopify_location_id}
                    t={t}
                />
            )}

            {step === 'splitting' && <div className="qcb-action-bar__status">{t('Splitting fulfillment…')}</div>}
            {step === 'getting-rate' && <div className="qcb-action-bar__status">{t('Getting shipping rate…')}</div>}
            {step === 'getting-slip' && <div className="qcb-action-bar__status">{t('Generating packing slip…')}</div>}

            {step === 'rate' && rateInfo && (
                <div className="qcb-action-bar__rate">
                    {rateInfo.fallback_reason && (
                        <div style={{
                            padding: '6px 10px', marginBottom: 8,
                            background: '#fff5d6', border: '1px solid #ecc94b',
                            borderRadius: 6, color: '#744210', fontSize: 12,
                        }}>
                            ⚠️ {rateInfo.fallback_reason}
                        </div>
                    )}
                    {isPuertoRico && (
                        <div style={{
                            padding: '6px 10px', marginBottom: 8,
                            background: '#eff6ff', border: '1px solid #93c5fd',
                            borderRadius: 6, color: '#1e3a8a', fontSize: 12,
                        }}>
                            🇵🇷 {t('Puerto Rico — UPS Ground. The label prints with three copies of Shopify\'s customs form; put all three in the pouch on the outside of the box.')}
                        </div>
                    )}
                    {rateInfo.service_code === '11' && (
                        <div style={{
                            padding: '6px 10px', marginBottom: 8,
                            background: '#eff6ff', border: '1px solid #93c5fd',
                            borderRadius: 6, color: '#1e3a8a', fontSize: 12,
                        }}>
                            🇨🇦 {t('Canada — UPS Standard. Customs are declared from the order prices; duties and brokerage are collected from the recipient on delivery.')}
                        </div>
                    )}
                    <div className="qcb-action-bar__rate-info">
                        <span className="qcb-action-bar__rate-carrier">📦 {rateInfo.carrier_name || t('UPS Ground')}</span>
                        <span className="qcb-action-bar__rate-details">
                            {rateInfo.package_name} · {rateInfo.total_weight_lb?.toFixed(2)} lb
                        </span>
                        <span className="qcb-action-bar__rate-price">
                            ${parseFloat(rateInfo.quoted_price).toFixed(2)} {rateInfo.currency}
                        </span>
                    </div>
                    <button className="qcb-action-btn qcb-action-btn--green" onClick={() => purchaseLabel()}>
                        {t('Confirm & Buy Label')}
                    </button>
                </div>
            )}

            {step === 'buying' && (
                <div className="qcb-action-bar__status">
                    {t('Purchasing label…')}
                    {rateInfo?.quoted_price != null && (
                        <span className="qcb-action-bar__rate-price" style={{ marginLeft: 8 }}>
                            {rateInfo.carrier_name || 'UPS Ground'} · ${parseFloat(rateInfo.quoted_price).toFixed(2)}
                        </span>
                    )}
                    <span className="qcb-action-bar__timer">{buyingSec}s</span>
                </div>
            )}

            {step === 'done' && (
                <div className="qcb-action-bar__done">
                    <span className="qcb-action-bar__done-text">✓ {t('Label purchased')}</span>
                    {!pdfUrl && (
                        <span className="qcb-action-bar__done-loading">{t('Loading PDF…')}</span>
                    )}
                    {pdfUrl && (
                        // Synchronous parent-window print of the pre-loaded
                        // iframe (rendered hidden below with desktop=1 to
                        // suppress its own auto-print). Reliable in iPad
                        // standalone PWA — see onParentPrintClick comment.
                        <button
                            type="button"
                            className="qcb-action-btn qcb-action-btn--blue"
                            onClick={onParentPrintClick}
                            title={t('Print the shipping label + packing slip')}
                        >
                            🖨 {t('Reprint Label')}
                        </button>
                    )}
                    <button className="qcb-action-btn qcb-action-btn--green" onClick={() => onDone('done')}>
                        {t('Back to Order Search')}
                    </button>
                </div>
            )}

            {step === 'pickup-done' && (
                <div className="qcb-action-bar__done">
                    <span className="qcb-action-bar__done-text">✓ {isBulkClubDelivery ? t('Order fulfilled. Ready for bulk shipping.') : t('Transferred to pickup location')}</span>
                    {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="qcb-action-bar__done-link">
                            🖨 {t('Open & Print Packing Slip')}
                        </a>
                    )}
                    {pdfUrl && (
                        <button
                            className="qcb-action-btn qcb-action-btn--blue"
                            onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                            title={t('Reopen the packing slip and re-fire the print dialog')}
                        >
                            🖨 {t('Reprint Packing Slip')}
                        </button>
                    )}
                    <button className="qcb-action-btn qcb-action-btn--green" onClick={() => onDone('done')}>
                        {t('Back to Order Search')}
                    </button>
                </div>
            )}

            {step === 'intl-done' && (
                <div className="qcb-action-bar__done">
                    <span className="qcb-action-bar__done-text" style={{ color: '#92400e' }}>
                        🌐 {t('Packing slip printed — finish in Shopify admin')}
                    </span>
                    {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="qcb-action-bar__done-link">
                            🖨 {t('Open & Print Packing Slip')}
                        </a>
                    )}
                    {pdfUrl && (
                        <button
                            className="qcb-action-btn qcb-action-btn--blue"
                            onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                            title={t('Reopen the packing slip and re-fire the print dialog')}
                        >
                            🖨 {t('Reprint Packing Slip')}
                        </button>
                    )}
                    <button className="qcb-action-btn qcb-action-btn--green" onClick={() => onDone('done')}>
                        {t('Back to Order Search')}
                    </button>
                </div>
            )}

            {step === 'nonground-done' && (
                <div className="qcb-action-bar__done">
                    <span className="qcb-action-bar__done-text" style={{ color: '#92400e' }}>
                        📦 {t('Packing slip printed — ship in Shopify admin')} ({t('customer paid for')} <strong>{paidShippingTitle}</strong>)
                    </span>
                    {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="qcb-action-bar__done-link">
                            🖨 {t('Open & Print Packing Slip')}
                        </a>
                    )}
                    {pdfUrl && (
                        <button
                            className="qcb-action-btn qcb-action-btn--blue"
                            onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                            title={t('Reopen the packing slip and re-fire the print dialog')}
                        >
                            🖨 {t('Reprint Packing Slip')}
                        </button>
                    )}
                    <button className="qcb-action-btn qcb-action-btn--green" onClick={() => onDone('done')}>
                        {t('Back to Order Search')}
                    </button>
                </div>
            )}

            {nonGroundModalOpen && (
                <div className="ppb__overscan-backdrop" onClick={() => setNonGroundModalOpen(false)}>
                    <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                        <div className="ppb__overscan-title">
                            📦 {t('Not a Ground Shipment')}
                        </div>
                        <div className="ppb__overscan-body">
                            {t('Please ship using the Shopify console. This is not a ground shipment, and the customer paid for')}
                            {' '}<strong>{paidShippingTitle}</strong>.
                            <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                                {t('The order is still split and the packing slip still prints — only the UPS Ground label is skipped.')}
                            </div>
                        </div>
                        <button
                            className="ppb__overscan-ok"
                            onClick={() => { setNonGroundModalOpen(false); splitAndNonGroundSlip(); }}
                        >
                            {t('Print Packing Slip')}
                        </button>
                        <button
                            className="ppb__overscan-ok"
                            style={{ marginTop: 8, background: '#6b7280' }}
                            onClick={() => setNonGroundModalOpen(false)}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            )}

            {intlModalOpen && (
                <div className="ppb__overscan-backdrop" onClick={() => setIntlModalOpen(false)}>
                    <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                        <div className="ppb__overscan-title">
                            🌐 {t('International Shipment')}
                        </div>
                        <div className="ppb__overscan-body">
                            {t('This is an international shipment, and will need to be shipped through the Shopify admin console.')}
                            <div style={{ marginTop: 10, fontSize: 13, color: '#374151' }}>
                                {intlDestinationLabel && <>{t('Destination')}: <strong>{intlDestinationLabel}</strong></>}
                            </div>
                        </div>
                        <button
                            className="ppb__overscan-ok"
                            onClick={() => { setIntlModalOpen(false); splitAndIntlSlip(); }}
                        >
                            {t('Print Packing Slip')}
                        </button>
                        <button
                            className="ppb__overscan-ok"
                            style={{ marginTop: 8, background: '#6b7280' }}
                            onClick={() => setIntlModalOpen(false)}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            )}

            {/* Partial-fulfillment confirmation (SP-297179). In-app modal
                that replaces the native window.confirm() surface —
                operators on the kiosk expect OpsHub styling, and the
                browser "operations-test.soccerpost.com says" bar reads
                like an error page. Backdrop click == Cancel to match the
                other QCActionBar modals; buttons resolve the promise the
                splitIfNeeded await is holding. */}
            {partialConfirm && (
                <div
                    className="ppb__overscan-backdrop"
                    onClick={() => { partialConfirm.resolve(false); setPartialConfirm(null); }}
                >
                    <div className="ppb__overscan-modal" onClick={e => e.stopPropagation()}>
                        <div className="ppb__overscan-title">
                            ⚠️ {t('Partial Fulfillment')}
                        </div>
                        <div className="ppb__overscan-body">
                            {t("You've scanned {shipping} of {totalOpen} item{s} at this location.", {
                                shipping:  partialConfirm.shipping,
                                totalOpen: partialConfirm.totalOpen,
                                s:         partialConfirm.totalOpen !== 1 ? 's' : '',
                            })}
                            <div style={{ marginTop: 10 }}>
                                {partialConfirm.remaining > 0
                                    ? t('Ship {shipping} item{ss} now and leave {remaining} item{rs} for a later shipment?', {
                                        shipping:  partialConfirm.shipping,
                                        ss:        partialConfirm.shipping !== 1 ? 's' : '',
                                        remaining: partialConfirm.remaining,
                                        rs:        partialConfirm.remaining !== 1 ? 's' : '',
                                    })
                                    : t('Ship {shipping} item{ss} now as a partial fulfillment?', {
                                        shipping: partialConfirm.shipping,
                                        ss:       partialConfirm.shipping !== 1 ? 's' : '',
                                    })}
                            </div>
                        </div>
                        <button
                            className="ppb__overscan-ok"
                            onClick={() => { partialConfirm.resolve(true); setPartialConfirm(null); }}
                        >
                            {t('Ship as Partial →')}
                        </button>
                        <button
                            className="ppb__overscan-ok"
                            style={{ marginTop: 8, background: '#6b7280' }}
                            onClick={() => { partialConfirm.resolve(false); setPartialConfirm(null); }}
                        >
                            {t('← Cancel & keep packing')}
                        </button>
                    </div>
                </div>
            )}

            {step === 'error' && (
                <div className="qcb-action-bar__error">
                    <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
                    <button
                        className="qcb-action-btn qcb-action-btn--blue"
                        onClick={() => {
                            // Wipe ALL transient label state so the next
                            // attempt creates a fresh ShippingLabel + session
                            // on Shopify (reusing a stale label_gid against
                            // an invalidated session always 404s).
                            setRateInfo(null);
                            setPdfUrl(null); setPdfRawUrl(null);
                            setLabelFoGid(null);
                            setSplitDone(false);
                            setError('');
                            setStep('idle');
                        }}
                    >
                        {t('Retry')}
                    </button>
                </div>
            )}

            {/* Pre-loaded combined-html iframe (full viewport, hidden by
                default). When the Reprint Label button is tapped we add
                'qcb-print-iframe-mode' to <body> and call window.print()
                synchronously — the @media print rule in styles.css then
                hides every other element and shows ONLY this iframe in the
                print preview. desktop=1 suppresses the popup HTML's own
                setTimeout-based auto-print so the iframe does NOT trigger
                an "automatic" print on its own load (which iPad Safari
                blocks with the Allow prompt). */}
            {pdfUrl && !desktopPrintUrl && (
                <iframe
                    ref={printIframeRef}
                    src={`${pdfUrl}&desktop=1`}
                    title="reprint-cache"
                    className="qcb-print-iframe-prerendered"
                    aria-hidden="true"
                />
            )}

            {/* Desktop-only: in-page modal with a full-bleed iframe. Avoids the
                focus-stealing new window that exits fullscreen on desktop
                Chrome. The iframe loads the same /buy-and-print-html or
                /packing-slip-print HTML the popup used to load — its embedded
                window.print() fires when the HTML finishes loading, opening
                Chrome's print preview over the fullscreen portal. */}
            {desktopPrintUrl && (
                <div
                    role="dialog"
                    aria-label="Print preview"
                    className="qcb-print-modal"
                    onKeyDown={(e) => { if (e.key === 'Escape') { closeDesktopModal(); } }}
                >
                    <div className="qcb-print-modal__header">
                        <span className="qcb-print-modal__title">
                            🖨 {orderInfo?.name || 'Print preview'}
                        </span>
                        <button
                            type="button"
                            className="qcb-print-modal__close"
                            aria-label="Close"
                            onClick={closeDesktopModal}
                        >✕</button>
                    </div>
                    <iframe
                        ref={desktopIframeRef}
                        src={desktopPrintUrl}
                        title="Print preview"
                        className="qcb-print-modal__iframe"
                    />
                </div>
            )}
        </div>
        {/* 2026-08-27b: the fulfilled-portion reprint row must NOT render
            alongside the main action bar — both are fixed-bottom
            .qcb-action-bar containers, and stacking them hid the primary
            Fulfill & Create Label button on partially-fulfilled orders with
            passed items (SP-340254, John C. — couldn't print at all). The
            row lives exclusively in the no-active-work early return above. */}
        </>
    );
}


// Action-bar variant rendered when the order has no open work for this
// location (already shipped / already transferred). Fetches stored
// shipping_labels for the order and offers Reprint buttons so operators can
// recover a label without bouncing out to Order Detail.
// Map our internal store_key to the Shopify admin store handle (myshopify
// subdomain). Used to deep-link operators to the order in Shopify Admin
// when OpsHub has no local label record (e.g. the label was bought in
// Shopify Admin directly, not through OpsHub's flow).
const SHOPIFY_ADMIN_HANDLE = {
    soccerpost: 'soccerpostshop',
    surf:       'surf-soccerpost',
    rush:       'rush-soccerpost',
    citysports: 'citysportsnj',
};

// Compact reprint strip rendered INSIDE the main action bar (same sticky
// container — never a second overlapping bar) for partially-fulfilled
// orders. Lets the operator reprint the already-shipped portion's label(s)
// while QC-ing the remainder (SP-351295, 2026-08-27). One button per stored
// label, oldest = "Label 1".
function InlineLabelReprints({ orderId, storeKey, shopifyLocationId, t }) {
    const [labels, setLabels] = useState([]);
    const [labelTok, setLabelTok] = useState('');
    const sk = storeKey || 'soccerpost';
    const jwt = (typeof window !== 'undefined' && window.localStorage)
        ? (localStorage.getItem('operations_token')
           || sessionStorage.getItem('pressing_admin_token')
           || localStorage.getItem('ops_portal_token')
           || '')
        : '';
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(`/api/orders/${orderId}/shipping-labels?store=${encodeURIComponent(sk)}`,
                    { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
                const j = r.ok ? await r.json() : { labels: [] };
                if (!cancelled) setLabels(Array.isArray(j) ? j : (j.labels || []));
            } catch (_) { if (!cancelled) setLabels([]); }
            try {
                const r2 = await fetch(`/api/label-token?store=${encodeURIComponent(sk)}`, {
                    method: 'POST', headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
                if (r2.ok) { const d2 = await r2.json(); if (!cancelled && d2?.token) setLabelTok(d2.token); }
            } catch (_) { /* fall back to session jwt */ }
        })();
        return () => { cancelled = true; };
    }, [orderId, sk, jwt]);
    const btns = [...labels].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return ta - tb;
    }).map((l, i, arr) => ({
        tail: String(l.shopify_label_gid || '').split('/').pop(),
        caption: arr.length > 1 ? `${t('Reprint Label')} ${i + 1}` : t('Reprint Label'),
    })).filter(b => b.tail);
    if (btns.length === 0) return null;
    const open = (tail) => {
        const src = `/api/shipping-labels/${encodeURIComponent(tail)}/combined-html`
            + `?store=${encodeURIComponent(sk)}&token=${encodeURIComponent(labelTok || jwt)}`;
        if (IS_STANDALONE_PWA) {
            window.location.href = buildPwaPrintUrl(`${src}&desktop=1`, orderId, sk, shopifyLocationId);
            return;
        }
        try { window.open(src, '', 'width=920,height=1100,scrollbars=yes,resizable=yes'); } catch (_) {}
    };
    return (
        // Bottom-right of the sticky action bar (user request 2026-08-27):
        // the primary Fulfill button stays centered; this floats right.
        <div style={{ position: 'absolute', right: 24, bottom: 16,
                      display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 13, color: '#6d7175', fontWeight: 600 }}>
                {t('Previously shipped:')}
            </span>
            {btns.map(b => (
                <button key={b.tail} type="button"
                        className="qcb-action-btn qcb-action-btn--blue"
                        style={{ padding: '8px 14px', fontSize: 14 }}
                        onClick={() => open(b.tail)}>
                    🖨 {b.caption}
                </button>
            ))}
        </div>
    );
}


function AlreadyDoneBar({ message, orderId, storeKey, shopifyLocationId, fetchFn, isShipped, isPickup, pickupFoGid, bulkFoGid, t }) {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    // H1: short-lived label token for the reprint URLs (below), so the session
    // JWT isn't put in the query string. Minted on mount; the header-based
    // fetches keep using the session jwt (headers don't leak to logs).
    const [labelTok, setLabelTok] = useState('');
    const reprintIframeRef = useRef(null);
    const sk = storeKey || 'soccerpost';
    // Kiosk portals store JWT under ops_portal_token; admin login stores under
    // operations_token; pressing-portal-as-admin uses sessionStorage. Cover
    // all three so this works in every entry path. (The default fetchFn only
    // reads operations_token, which is null in kiosk mode → 401.)
    const jwt = (typeof window !== 'undefined' && window.localStorage)
        ? (localStorage.getItem('operations_token')
           || sessionStorage.getItem('pressing_admin_token')
           || localStorage.getItem('ops_portal_token')
           || '')
        : '';
    useEffect(() => {
        let cancelled = false;
        const fetchLabels = async () => {
            const url = `/api/orders/${orderId}/shipping-labels?store=${encodeURIComponent(sk)}`;
            const r = await fetch(url, {
                headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
            });
            if (!r.ok) throw new Error('lookup failed');
            const j = await r.json();
            return Array.isArray(j) ? j : (j.labels || []);
        };
        (async () => {
            try {
                let arr = await fetchLabels();
                // Self-heal: if our DB has no labels for a shipped order, ask
                // the backend to ingest any existing labels from Shopify
                // (e.g. labels purchased outside OpsHub). Then re-fetch.
                if (!cancelled && arr.length === 0 && isShipped) {
                    try {
                        await fetch(
                            `/api/orders/${orderId}/shipping-labels/recover?store=${encodeURIComponent(sk)}`,
                            {
                                method: 'POST',
                                headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
                            },
                        );
                        arr = await fetchLabels();
                    } catch (_) { /* fall through to empty state */ }
                }
                if (!cancelled) setLabels(arr);
            } catch (_) {
                if (!cancelled) setLabels([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orderId, sk, jwt, isShipped]);

    // H1: mint the short-lived label token once on mount, for the reprint URLs.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await fetch(`/api/label-token?store=${encodeURIComponent(sk)}`, {
                    method: 'POST',
                    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
                });
                if (alive && r.ok) { const d = await r.json(); if (d && d.token) setLabelTok(d.token); }
            } catch (_) { /* keep empty → falls back to session jwt below */ }
        })();
        return () => { alive = false; };
    }, [sk, jwt]);

    // Render the "already fulfilled" message and (when there's a stored
    // label) the Reprint button on a single row. Compute the active label up
    // front so we can decide whether to render the button alongside the
    // message before opening the row.
    // 2026-08-31 (SP-322685): on split-fulfillment orders, prefer the label
    // whose FO belongs to THIS portal's location — "newest overall" printed
    // the other warehouse's label when its row happened to be newer. Rows
    // without a stamped location keep the legacy newest-first behavior.
    const _locLabels = shopifyLocationId
        ? labels.filter(l => String(l.shopify_location_id || '') === String(shopifyLocationId))
        : [];
    const _labelPool = _locLabels.length > 0 ? _locLabels : labels;
    const activeLabel = !loading && _labelPool.length > 0
        ? [..._labelPool].sort((a, b) => {
            const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            if (tb !== ta) return tb - ta;
            return (Number(b.id) || 0) - (Number(a.id) || 0);
          })[0]
        : null;
    const activeTail = activeLabel ? String(activeLabel.shopify_label_gid || '').split('/').pop() : '';

    // 2026-08-27: multi-shipment partials carry one label PER shipped
    // portion — every one stays reprintable (SP-351295). Oldest first so
    // "Label 1" is the first shipment.
    const labelBtns = !loading && labels.length > 0
        ? [...labels].sort((a, b) => {
            const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            return ta - tb;
          }).map((l, i, arr) => ({
            tail: String(l.shopify_label_gid || '').split('/').pop(),
            caption: arr.length > 1 ? `${t('Reprint Label')} ${i + 1}` : t('Reprint Label'),
          })).filter(b => b.tail)
        : [];

    // Pickup orders never have a shipping label — when the order is already
    // done at the pickup store, the operator just needs to reprint the
    // packing slip. Build a slip URL when we have a pickup FO id.
    // skip_transfer=true because the FO has already moved to the pickup
    // location; we don't want to re-trigger a transfer on each reprint tap.
    const slipUrl = isPickup && pickupFoGid
        ? `/api/orders/${orderId}/packing-slip-print`
            + `?fulfillment_order_id=${encodeURIComponent(pickupFoGid)}`
            + `&store=${encodeURIComponent(sk)}`
            + `&token=${encodeURIComponent(labelTok || jwt)}`
            + `&skip_transfer=true`
        : '';

    // Bulk Club Delivery slip reprint. Bulk orders carry no shipping label and
    // aren't pickups, so neither branch below fires — build the bulk-delivery
    // slip URL directly. skip_transfer=true is belt-and-suspenders (the backend
    // already forces is_pickup=false for bulk, so it never transfers); the
    // backend stamps the "BULK DELIVERY" header from the FO presentedName.
    const bulkSlipUrl = bulkFoGid
        ? `/api/orders/${orderId}/packing-slip-print`
            + `?fulfillment_order_id=${encodeURIComponent(bulkFoGid)}`
            + `&store=${encodeURIComponent(sk)}`
            + `&token=${encodeURIComponent(labelTok || jwt)}`
            + `&skip_transfer=true`
        : '';

    // Resolve which print URL the inline Reprint button should drive — prefer
    // a real shipping label when one exists, then the bulk slip, then pickup.
    let reprintIframeSrc = '';
    let reprintLabel = '';
    if (activeTail) {
        reprintIframeSrc = `/api/shipping-labels/${encodeURIComponent(activeTail)}/combined-html`
            + `?store=${encodeURIComponent(sk)}&token=${encodeURIComponent(labelTok || jwt)}&desktop=1`;
        reprintLabel = t('Reprint Label');
    } else if (bulkSlipUrl) {
        reprintIframeSrc = `${bulkSlipUrl}&desktop=1`;
        reprintLabel = t('Reprint Bulk Delivery Slip');
    } else if (slipUrl) {
        reprintIframeSrc = `${slipUrl}&desktop=1`;
        reprintLabel = t('Reprint Packing Slip');
    }
    const openLabelReprint = (tail) => {
        const src = `/api/shipping-labels/${encodeURIComponent(tail)}/combined-html`
            + `?store=${encodeURIComponent(sk)}&token=${encodeURIComponent(labelTok || jwt)}`;
        if (IS_STANDALONE_PWA) {
            window.location.href = buildPwaPrintUrl(`${src}&desktop=1`, orderId, sk, shopifyLocationId);
            return;
        }
        // iPad Safari tab (iPadOS 27) takes the same popup as desktop: the UNCHANGED label + slip
        // document, which auto-prints itself (Eric 2026-10-14: output must match the pre-iOS-27 print).
        try { window.open(src, '', 'width=920,height=1100,scrollbars=yes,resizable=yes'); } catch (_) {}
    };

    const onReprintTap = () => {
        if (!reprintIframeSrc) return;
        if (IS_STANDALONE_PWA) {
            window.location.href = buildPwaPrintUrl(reprintIframeSrc, orderId, sk, shopifyLocationId);
            return;
        }
        // 2026-08-27: desktop used to print the PARENT page with the label
        // document inside a hidden iframe (qcb-print-iframe-mode). That
        // flattens the multi-page document — the 4×6 label page + letter
        // packing slip @page rules only apply when the label DOCUMENT
        // itself prints, so both pages got squeezed onto one clipped sheet
        // ("1 sheet of paper" in the Chrome preview). Open it in a popup
        // instead; the document auto-prints itself with correct page sizes.
        const popupUrl = reprintIframeSrc.replace('&desktop=1', '');
        try {
            const w = window.open(popupUrl, '', 'width=920,height=1100,scrollbars=yes,resizable=yes');
            if (w) return;
        } catch (_) { /* popup blocked — fall through to legacy iframe print */ }
        document.body.classList.add('qcb-print-iframe-mode');
        try { window.print(); } catch (_) {}
        const off = () => {
            document.body.classList.remove('qcb-print-iframe-mode');
            window.removeEventListener('afterprint', off);
        };
        window.addEventListener('afterprint', off);
        setTimeout(off, 5000);
    };

    return (
        <div className="qcb-action-bar">
            <div className="qcb-action-bar__row qcb-action-bar__row--done">
                <div className="qcb-action-bar__status qcb-action-bar__status--done">
                    {message}
                </div>
                {labelBtns.length > 0 ? (
                    labelBtns.map(b => (
                        <button
                            key={b.tail}
                            type="button"
                            className="qcb-action-btn qcb-action-btn--blue"
                            onClick={() => openLabelReprint(b.tail)}
                        >
                            🖨 {b.caption}
                        </button>
                    ))
                ) : (reprintIframeSrc && (
                    <button
                        type="button"
                        className="qcb-action-btn qcb-action-btn--blue"
                        onClick={onReprintTap}
                    >
                        🖨 {reprintLabel}
                    </button>
                ))}
            </div>
            {reprintIframeSrc && (
                // Hidden pre-loaded iframe so non-PWA (desktop, in-browser
                // iPad) Reprint can fire window.print() synchronously inside
                // the user-gesture handler — no async iframe load = no
                // 'automatic print' warning.
                <iframe
                    ref={reprintIframeRef}
                    src={reprintIframeSrc}
                    title="reprint-cache"
                    className="qcb-print-iframe-prerendered"
                    aria-hidden="true"
                />
            )}
        </div>
    );
}
